import { useState, useEffect, useRef } from 'react';
import { API } from '@/lib/axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { FileText, ChevronLeft, ChevronRight, WifiOff, Cloud, RefreshCw, Zap, CheckCircle, Clock } from 'lucide-react';
import AuthedImage from '@/components/AuthedImage';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import * as pdfjsLib from 'pdfjs-dist';
import { useOffline } from '@/contexts/OfflineContext';
import { getOfflineJSAs, queueJSASignature } from '@/services/syncService';
import { saveBulkToStore } from '@/services/offlineDB';
import { useTranslation } from 'react-i18next';

const WorkerJSAs = ({ user }) => {
  const { t } = useTranslation();
  const [jsas, setJsas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJsa, setSelectedJsa] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [lotoEquipment, setLotoEquipment] = useState({});
  const [verifyingKey, setVerifyingKey] = useState(null);
  const [showSdsModal, setShowSdsModal] = useState(false);
  const [selectedSds, setSelectedSds] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingSds, setLoadingSds] = useState(false);
  const [isOfflineData, setIsOfflineData] = useState(false);
  const canvasRef = useRef(null);
  
  // Offline context
  const { isOnline, isOffline, updatePendingCount } = useOffline();

  useEffect(() => {
    loadJsas();
  }, [isOnline]);

  const loadJsas = async () => {
    setLoading(true);
    
    // Try online first, fall back to offline
    if (isOnline) {
      try {
        const response = await axiosInstance.get('/jsa');
        setJsas(response.data);
        setIsOfflineData(false);
        
        // Cache JSAs for offline use
        if (response.data && response.data.length > 0) {
          await saveBulkToStore('jsas', response.data);
          console.log('📦 Cached', response.data.length, 'JSAs for offline');
        }
      } catch (error) {
        console.error('Failed to load JSAs online, trying offline:', error);
        await loadOfflineJsas();
      }
    } else {
      await loadOfflineJsas();
    }
    
    setLoading(false);
  };
  
  const loadOfflineJsas = async () => {
    try {
      const offlineJsas = await getOfflineJSAs();
      if (offlineJsas && offlineJsas.length > 0) {
        setJsas(offlineJsas);
        setIsOfflineData(true);
        toast.info(t('audits.showingCachedAudits'), { duration: 3000 });
      } else {
        setJsas([]);
        toast.warning(t('audits.noCachedAudits'), { duration: 4000 });
      }
    } catch (error) {
      console.error('Failed to load offline JSAs:', error);
      setJsas([]);
    }
  };

  const openJsaDialog = async (jsa) => {
    setSelectedJsa(jsa);
    setSignatureName(user.name || '');
    setAcknowledged(false);
    setShowDialog(true);
    setLotoEquipment({});

    const lotoHazards = (jsa.hazards || []).filter(
      (h) => h?.source === 'company_loto_sop' && h?.equipmentId
    );
    const eqIds = [...new Set(lotoHazards.map((h) => h.equipmentId))];
    if (eqIds.length === 0) return;
    try {
      const [{ data: verData }, ...eqResponses] = await Promise.all([
        axiosInstance.get(`/loto/jsa/${jsa.id}/verifications`),
        ...eqIds.map((id) => axiosInstance.get(`/loto/equipment/${id}`)),
      ]);
      const verifications = verData?.verifications || [];
      const map = {};
      eqResponses.forEach((r, idx) => {
        map[eqIds[idx]] = {
          equipment: r.data,
          verifications: verifications.filter((v) => v.equipmentId === eqIds[idx]),
        };
      });
      setLotoEquipment(map);
    } catch (e) {
      console.error('Failed to load LOTO details', e);
    }
  };

  const verifyLotoPoint = async (equipmentId, point) => {
    const key = `${equipmentId}-${point.sequence}`;
    if (verifyingKey === key) return;
    setVerifyingKey(key);
    try {
      const eq = lotoEquipment[equipmentId]?.equipment || {};
      const { data: rec } = await axiosInstance.post(
        `/loto/jsa/${selectedJsa.id}/verify`,
        {
          equipmentId,
          equipmentName: eq.equipmentName || '',
          sequence: point.sequence,
          location: point.location || '',
          energyType: point.energyType || '',
          verified: true,
        }
      );
      setLotoEquipment((prev) => ({
        ...prev,
        [equipmentId]: {
          ...prev[equipmentId],
          verifications: [...(prev[equipmentId]?.verifications || []), rec],
        },
      }));
      toast.success(`Verified: ${point.location || 'Point ' + point.sequence}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyingKey(null);
    }
  };

  const retractLotoPoint = async (equipmentId, point) => {
    const reason = window.prompt(
      `Retract verification for "${point.location || 'Point ' + point.sequence}"?\nOptional: enter a reason for the audit trail.`,
      ''
    );
    if (reason === null) return; // user canceled
    const key = `${equipmentId}-${point.sequence}`;
    setVerifyingKey(key);
    try {
      const { data: rec } = await axiosInstance.post(
        `/loto/jsa/${selectedJsa.id}/retract`,
        { equipmentId, sequence: point.sequence, reason }
      );
      setLotoEquipment((prev) => ({
        ...prev,
        [equipmentId]: {
          ...prev[equipmentId],
          verifications: [...(prev[equipmentId]?.verifications || []), rec],
        },
      }));
      toast.success('Verification retracted (audit trail preserved)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retract failed');
    } finally {
      setVerifyingKey(null);
    }
  };

  const isPointVerified = (equipmentId, seq) => {
    const verifs = lotoEquipment[equipmentId]?.verifications || [];
    // Sort by verifiedAt ASC, latest wins
    const forThis = verifs
      .filter((v) => v.sequence === seq)
      .slice()
      .sort((a, b) => (a.verifiedAt || '').localeCompare(b.verifiedAt || ''));
    const latest = forThis[forThis.length - 1];
    return latest && latest.verified && !latest.retracted ? latest : null;
  };

  const lotoPhotoUrl = (id) =>
    id ? `${API}/loto/photo/${id}` : null;

  const allLotoPointsVerified = () => {
    for (const eqId of Object.keys(lotoEquipment)) {
      const points = lotoEquipment[eqId]?.equipment?.lockoutPoints || [];
      for (const p of points) {
        if (!isPointVerified(eqId, p.sequence)) return false;
      }
    }
    return true;
  };

  useEffect(() => {
    // Set PDF.js worker - use CDN for reliable loading
    if (typeof pdfjsLib !== 'undefined') {
      const version = pdfjsLib.version || '4.4.168';
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.mjs`;
      console.log('PDF.js initialized with worker URL:', pdfjsLib.GlobalWorkerOptions.workerSrc);
    } else {
      console.error('PDF.js library not loaded!');
    }
  }, []);

  useEffect(() => {
    if (pdfDoc && canvasRef.current) {
      renderPage(currentPage);
    }
  }, [currentPage, pdfDoc]);

  const renderPage = async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;
    
    try {
      const page = await pdfDoc.getPage(pageNum);
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Calculate scale to fit container width
      const container = canvas.parentElement;
      const containerWidth = container.clientWidth - 32; // padding
      const viewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / viewport.width;
      
      // Use device pixel ratio for sharp rendering on high-DPI screens
      const outputScale = window.devicePixelRatio || 1;
      const scaledViewport = page.getViewport({ scale: scale * outputScale });
      
      // Set canvas size with device pixel ratio for crisp rendering
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${scaledViewport.height / outputScale}px`;
      
      // Scale context to match device pixel ratio
      context.scale(outputScale, outputScale);
      
      await page.render({
        canvasContext: context,
        viewport: page.getViewport({ scale })
      }).promise;
    } catch (error) {
      console.error('Error rendering page:', error);
    }
  };

  const viewSds = async (sdsId, chemicalName) => {
    setSelectedSds({ sdsId, chemicalName });
    setShowSdsModal(true);
    setLoadingSds(true);
    setCurrentPage(1);
    
    try {
      console.log('Fetching SDS:', sdsId);
      console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);
      
      const response = await axiosInstance.get(`/sds/download/${sdsId}`, {
        responseType: 'arraybuffer',
        timeout: 30000 // 30 second timeout
      });
      
      console.log('SDS downloaded, size:', response.data.byteLength);
      
      if (response.data.byteLength === 0) {
        throw new Error('Empty PDF file received');
      }
      
      const loadingTask = pdfjsLib.getDocument({ 
        data: response.data,
        cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.4.449/cmaps/',
        cMapPacked: true
      });
      const pdf = await loadingTask.promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      console.log('PDF loaded successfully, pages:', pdf.numPages);
      toast.success(t('jsa.sdsLoaded'));
    } catch (error) {
      console.error('Failed to load SDS - Full error:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      
      let errorMsg = 'Failed to load SDS document';
      if (error.response?.status === 404) {
        errorMsg = 'SDS not found. Please ensure it was uploaded to the library.';
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        errorMsg = 'Authentication error. Please log out and log back in.';
      } else if (error.message?.includes('timeout')) {
        errorMsg = 'Request timed out. Please check your internet connection.';
      } else if (error.message?.includes('Network')) {
        errorMsg = 'Network error. Please check your connection.';
      } else if (error.message?.includes('worker')) {
        errorMsg = 'PDF viewer initialization failed. Please refresh the page.';
      }
      
      toast.error(errorMsg + ' - Opening in browser instead...');
      setShowSdsModal(false);
      
      // Fallback: Try to open in new tab if PDF.js fails
      if (error.message?.includes('worker') || error.message?.includes('PDF')) {
        setTimeout(async () => {
          try {
            const response = await axiosInstance.get(`/sds/download/${sdsId}`, {
              responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (fallbackError) {
            console.error('Fallback also failed:', fallbackError);
          }
        }, 1000);
      }
    } finally {
      setLoadingSds(false);
    }
  };

  const downloadSds = async (sdsId, chemicalName) => {
    try {
      toast.info(t('jsa.downloadingSds'));
      const response = await axiosInstance.get(`/sds/download/${sdsId}`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${chemicalName || 'SDS'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('jsa.sdsDownloaded'));
    } catch (error) {
      console.error('Download failed:', error);
      const errorMsg = error.response?.data?.detail || t('jsa.failedSdsDownload');
      toast.error(errorMsg);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const signJsa = async () => {
    if (!signatureName || signatureName.trim() === '') {
      toast.error(t('jsa.enterNameToSign'));
      return;
    }

    if (!acknowledged) {
      toast.error(t('jsa.confirmReadJsa'));
      return;
    }

    const signatureData = {
      signature: signatureName,
      userId: user.id,
      userName: user.name,
      signedAt: new Date().toISOString()
    };

    // If offline, queue for later sync
    if (isOffline) {
      try {
        await queueJSASignature(selectedJsa.id, signatureData);
        await updatePendingCount();
        
        // Update local state to show as signed
        setJsas(prev => prev.map(jsa => {
          if (jsa.id === selectedJsa.id) {
            return {
              ...jsa,
              workerAcknowledgments: {
                ...jsa.workerAcknowledgments,
                [user.id]: { acknowledged: true, signedAt: signatureData.signedAt, signature: signatureName, pendingSync: true }
              }
            };
          }
          return jsa;
        }));
        
        toast.success('✅ Signature saved! Will sync when online.', { duration: 4000 });
        setShowDialog(false);
      } catch (error) {
        console.error('Failed to queue signature:', error);
        toast.error(t('jsa.failedSignSave'));
      }
      return;
    }

    // Online - submit directly
    try {
      await axiosInstance.post(`/jsa/${selectedJsa.id}/acknowledge?signature=${encodeURIComponent(signatureName)}`);
      toast.success(t('jsa.jsaSigned'));
      setShowDialog(false);
      await loadJsas();
    } catch (error) {
      console.error('Failed to sign JSA:', error);
      toast.error(error.response?.data?.detail || t('common.saveFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Offline Mode Banner - shows when offline OR when showing cached data */}
        {(!isOnline || isOfflineData) && (
          <div className={`mb-4 p-3 ${!isOnline ? 'bg-orange-100 border-orange-300' : 'bg-orange-50 border-orange-200'} border rounded-lg flex items-center gap-3`}>
            <WifiOff className={`w-5 h-5 ${!isOnline ? 'text-orange-700' : 'text-orange-600'} flex-shrink-0`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${!isOnline ? 'text-orange-900' : 'text-orange-800'}`}>
                {!isOnline ? t('common.offlineMode') : t('common.viewingCachedData')}
              </p>
              <p className={`text-xs ${!isOnline ? 'text-orange-700' : 'text-orange-600'}`}>
                {!isOnline 
                  ? t('jsa.offlineSignNote')
                  : t('common.connectForUpdates')}
              </p>
            </div>
            {isOnline && (
              <Button size="sm" variant="outline" onClick={loadJsas} className="flex-shrink-0">
                <RefreshCw className="w-4 h-4 mr-1" />
                {t('common.refresh')}
              </Button>
            )}
          </div>
        )}
        
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4 hover:bg-gray-100"
        >
          ← {t('common.back')}
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('jsa.title')}</h1>
          <p className="text-gray-600 mt-1">{t('jsa.viewAndSignDesc')}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('jsa.myJsas')}</CardTitle>
            <CardDescription>{t('jsa.reviewAndAcknowledge')}</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="text-slate-500">{t('jsa.loadingJsas')}</div>
              </div>
            ) : jsas.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">{t('jsa.noJsasAssigned')}</h3>
                <p className="text-slate-500">{t('jsa.noJsasAssignedDesc')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {jsas.map((jsa) => {
                  const hasAcknowledged = jsa.workerAcknowledgments?.[user.id]?.acknowledged;
                  const requiresSignature = jsa.status === 'pending_signatures';
                  
                  return (
                    <div
                      key={jsa.id}
                      onClick={() => openJsaDialog(jsa)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        !hasAcknowledged && requiresSignature
                          ? 'bg-orange-50 border-orange-200 shadow-sm'
                          : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <FileText className={`w-5 h-5 mt-0.5 ${!hasAcknowledged && requiresSignature ? 'text-orange-600' : 'text-slate-400'}`} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">
                                {jsa.taskDescription}
                              </h4>
                              {!hasAcknowledged && requiresSignature && (
                                <Badge variant="destructive" className="text-xs">{t('jsa.signatureRequired')}</Badge>
                              )}
                              {hasAcknowledged && (
                                <Badge className="text-xs bg-green-100 text-green-800">{t('common.signed')}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">
                              {t('common.location')}: {jsa.location} | {t('common.date')}: {jsa.date}
                            </p>
                            <p className="text-sm text-slate-600">
                              {t('jsa.supervisor')}: {jsa.supervisorName}
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                              {jsa.hazards?.length || 0} {t('jsa.hazardsIdentified')}
                            </p>
                            {hasAcknowledged && jsa.workerAcknowledgments[user.id] && (
                              <p className="text-xs text-green-600 mt-1">
                                ✓ Signed on {new Date(jsa.workerAcknowledgments[user.id].timestamp).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* JSA Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('jsa.jsaDetails')}</DialogTitle>
            <DialogDescription>Review and sign this JSA</DialogDescription>
          </DialogHeader>
          
          {selectedJsa && (
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold text-sm text-slate-700">Task Description</h4>
                <p className="text-slate-600">{selectedJsa.taskDescription}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-slate-700">Location</h4>
                  <p className="text-slate-600">{selectedJsa.location}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-slate-700">Date</h4>
                  <p className="text-slate-600">{selectedJsa.date}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-sm text-slate-700 mb-2">Hazards & Controls</h4>
                {selectedJsa.hazards?.map((hazard, index) => {
                  const isLoto = hazard?.source === 'company_loto_sop';
                  const eqDetail = isLoto && hazard?.equipmentId ? lotoEquipment[hazard.equipmentId] : null;
                  const points = eqDetail?.equipment?.lockoutPoints || [];
                  return (
                  <div
                    key={index}
                    className={`mb-3 p-3 rounded border ${isLoto ? 'bg-orange-50 border-l-4 border-l-orange-500' : 'bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      {isLoto ? <Zap className="w-4 h-4 text-orange-600" /> : <span>⚠️</span>}
                      <p className="font-medium text-slate-800">{hazard.hazard}</p>
                      {isLoto && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full">
                          From your LOTO SOP
                        </span>
                      )}
                    </div>
                    {isLoto && hazard.sourceDocumentTitle && (
                      <p className="text-[11px] text-orange-700 mb-2 italic">Source: {hazard.sourceDocumentTitle}</p>
                    )}
                    {isLoto && points.length > 0 ? (
                      <div className="space-y-2 mt-2 bg-white border border-orange-200 rounded p-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-semibold text-orange-900">Confirm each lockout point on-site</p>
                          <span className="text-[10px] text-orange-800">29 CFR 1910.147</span>
                        </div>
                        {points
                          .slice()
                          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                          .map((p) => {
                            const verified = isPointVerified(hazard.equipmentId, p.sequence);
                            const key = `${hazard.equipmentId}-${p.sequence}`;
                            return (
                              <div
                                key={key}
                                className={`rounded p-2 border ${verified ? 'bg-green-50 border-green-300' : 'bg-white border-gray-300'}`}
                                data-testid={`loto-point-${key}`}
                              >
                                <div className="flex items-start gap-2">
                                  <div className="pt-0.5 min-w-[44px] min-h-[44px] flex items-center justify-center">
                                    {verified ? (
                                      <CheckCircle className="w-6 h-6 text-green-600" />
                                    ) : (
                                      <Checkbox
                                        checked={false}
                                        disabled={verifyingKey === key}
                                        onCheckedChange={() => verifyLotoPoint(hazard.equipmentId, p)}
                                        data-testid={`loto-verify-${key}`}
                                        className="w-6 h-6"
                                      />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs font-bold text-gray-700">#{p.sequence}</span>
                                      <Badge className="bg-yellow-100 text-yellow-900 hover:bg-yellow-100 text-[10px]">{p.energyType}</Badge>
                                      <span className="text-sm font-medium text-gray-900">{p.location}</span>
                                    </div>
                                    {p.lockoutMethod && (
                                      <p className="text-xs text-gray-700 mt-0.5">Method: {p.lockoutMethod}</p>
                                    )}
                                    {p.verification && (
                                      <p className="text-xs text-emerald-700 mt-0.5">Verify: {p.verification}</p>
                                    )}
                                    {p.photoGridfsId && (
                                      <div className="mt-2" data-testid={`loto-photo-link-${key}`}>
                                        <AuthedImage
                                          gridfsId={p.photoGridfsId}
                                          alt={`LOTO ${p.sequence}`}
                                          className="rounded border max-h-40 object-cover cursor-zoom-in"
                                        />
                                      </div>
                                    )}
                                    {verified && (
                                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        <p className="text-[11px] text-green-700 flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          Confirmed by {verified.userName} at {new Date(verified.verifiedAt).toLocaleString()}
                                        </p>
                                        <button
                                          type="button"
                                          onClick={() => retractLotoPoint(hazard.equipmentId, p)}
                                          className="text-[11px] text-red-700 hover:text-red-900 underline"
                                          data-testid={`loto-retract-${key}`}
                                          disabled={verifyingKey === key}
                                        >
                                          Retract
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-700 mb-2"><strong>Control:</strong> {hazard.mitigation || hazard.control || 'No control specified'}</p>
                    )}
                    {hazard.ppe && (
                      <p className="text-sm text-slate-700 mt-2"><strong>🛡️ Required PPE:</strong> {hazard.ppe}</p>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Attached Safety Data Sheets */}
              {selectedJsa.linkedSdsDocuments && selectedJsa.linkedSdsDocuments.length > 0 && (
                <div className="mt-4 p-3 bg-orange-50 border-2 border-orange-200 rounded">
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">📋 Attached Safety Data Sheets</h4>
                  {selectedJsa.linkedSdsDocuments.map((sds, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border mb-2">
                      <div>
                        <p className="text-sm font-medium">{sds.chemical_name}</p>
                        {sds.manufacturer && <p className="text-xs text-slate-600">{sds.manufacturer}</p>}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewSds(sds.sds_id, sds.chemical_name)}
                        className="text-xs"
                      >
                        {t('jsa.viewSds')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-3 pt-4 border-t">
                {!allLotoPointsVerified() && Object.keys(lotoEquipment).length > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded p-3 flex items-start gap-2" data-testid="loto-required-banner">
                    <Zap className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-orange-900">Confirm every LOTO point before you can sign</p>
                      <p className="text-orange-800 text-xs mt-1">
                        Each lockout point above needs a tap-to-verify. This creates a timestamped record for OSHA (29 CFR 1910.147).
                      </p>
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="signature">{t('jsa.yourNameSignature')}</Label>
                  <Input
                    id="signature"
                    value={signatureName}
                    onChange={(e) => setSignatureName(e.target.value)}
                    placeholder={t('jsa.enterFullName')}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="acknowledge"
                    checked={acknowledged}
                    onCheckedChange={setAcknowledged}
                  />
                  <label
                    htmlFor="acknowledge"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {t('jsa.acknowledgeJsa')}
                  </label>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={signJsa}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!allLotoPointsVerified()}
              data-testid="worker-sign-jsa-btn"
              title={!allLotoPointsVerified() ? 'Confirm every LOTO point before signing' : ''}
            >
              {t('jsa.signJsa')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SDS Viewer Modal */}
      <Dialog open={showSdsModal} onOpenChange={(open) => {
        setShowSdsModal(open);
        if (!open) {
          setPdfDoc(null);
          setCurrentPage(1);
          setTotalPages(0);
        }
      }}>
        <DialogContent className="max-w-4xl h-[95vh] p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              {selectedSds?.chemicalName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto p-4 bg-slate-50">
            {loadingSds ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-600">Loading SDS document...</p>
              </div>
            ) : pdfDoc ? (
              <div className="bg-white rounded shadow-sm p-4">
                <canvas ref={canvasRef} className="w-full" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <FileText className="h-16 w-16 text-gray-300 mb-4" />
                <p className="text-gray-600 mb-4">Unable to display PDF in viewer</p>
                <Button 
                  onClick={() => downloadSds(selectedSds?.sdsId, selectedSds?.chemicalName)}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Download SDS Instead
                </Button>
              </div>
            )}
          </div>
          
          {!loadingSds && totalPages > 0 && (
            <div className="px-6 py-3 border-t shrink-0 flex items-center justify-between bg-white">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              
              <span className="text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
          
          <DialogFooter className="px-6 py-3 border-t shrink-0 flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => downloadSds(selectedSds?.sdsId, selectedSds?.chemicalName)}
            >
              Download PDF
            </Button>
            <Button variant="outline" onClick={() => setShowSdsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerJSAs;